import ABCyaPhaser from '@abcya/abcya_phaser';
import Phaser from 'phaser'
import Fraction from '../game/Fraction';
import PuzzleLayout from '../game/PuzzleLayout';
import gameData from '../gameData';
import { musicDown } from '../JingleController';
import SoundAssetKeys from '../SoundAssetKeys';
import CombineContainer from '../ui/CombineContainer';
import GameHud from '../ui/GameHud';
import GridDisplay from '../ui/GridDisplay';
import Results from './Results';

export default class Game extends Phaser.Scene
{
    static get KEY() { return 'Game'; }

    constructor()
    {
        super({ key: (Game).KEY });
    }

    init()
    {
        this.hud = null;

        this.grid = null;
        this.gridSize = 4;
        this.gridDisplay = null;

        this.selectedTiles = null;

        this.busyAnimating = false;
        this.combineContainer = null;

        this.gameOver = false;
        this.wrongCount = 0;
        this.correctCount = 0;

        this.crumbPool = null;
        this.crumbs = null;
    }

    create()
    {
        musicDown(this);

        this.add.image(0, 0, "bg").setOrigin(0);

        this.wrongCount = 0;
        this.correctCount = 0;

        this.hud = new GameHud(this);
        this.hud.monster.delegate = this;
        this.add.existing(this.hud);

        this.grid = new PuzzleLayout(this);
        this.grid.reset(this.gridSize, this.gridSize);

        this.gridDisplay = new GridDisplay(this);
        this.gridDisplay.x = 24;
        this.gridDisplay.y = 24;

        this.add.existing(this.gridDisplay);

        this.combineContainer = new CombineContainer(this).setVisible(false);
        this.add.existing(this.combineContainer);

        this.initializePuzzleFractions();
        this.initializeGridDisplay();

        this.busyAnimating = true;
    }

    initializePuzzleFractions()
    {
        let len = this.gridSize * this.gridSize;
        let allFractions = [];
        let f = null;
        let denominatorPool = new RandomValuePool([2, 3, 4, 5, 6, 7, 8, 9]);
        for (let i = 0; i < len / 2; i++)
        {
            f = this.getFractionSet(denominatorPool.getValue());
            allFractions.push(f[0]);
            allFractions.push(f[1]);
        }
        Phaser.Utils.Array.Shuffle(allFractions);
        for (let i = 0; i < len; i++)
        {
            this.grid.setTileValue(i, allFractions[i]);
        }
    }

    getFractionSet(den)
    {
        let num1 = 0, num2 = 0;
        let min = 1;
        let max = den - 1;
        num1 = Phaser.Math.Between(min, max);
        num2 = den - num1;
        let f1 = new Fraction(num1, den);
        let f2 = new Fraction(num2, den);

        return [f1, f2];
    }

    initializeGridDisplay()
    {
        let wait = this.gridDisplay.displayPuzzle(this.grid.spotMatrix);
        this.selectedTiles = [];
        this.time.delayedCall(wait, this.startGame, null, this);
    }

    startGame()
    {
        this.busyAnimating = false;
        this.hud.startTime();
    }

    tileClick(tile)
    {
        if (this.busyAnimating || this.gameOver) return;
        if (tile.isUsed) return;

        ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._click, this);
        if (this.selectedTiles.length === 0)
        {
            this.selectedTiles.push(tile);
            tile.select();
        }
        else if (this.selectedTiles.length === 1)
        {
            if (this.selectedTiles.indexOf(tile) >= 0)
            {
                tile.unselect();
                this.selectedTiles = [];
            }
            else
            {
                this.selectedTiles.push(tile);
                tile.select();
                this.busyAnimating = true;
                this.gridDisplay.hold();
                this.checkSelectionAnswer();
            }
        }
    }

    checkSelectionAnswer()
    {
        let f1 = this.selectedTiles[0];
        let f2 = this.selectedTiles[1];
        let correct = false;

        // new code that allows for different fraction denominators to be selected and equal 1
        let f1Numerator = f1.fractionValue.numerator * f2.fractionValue.denominator;
        let f2Numerator = f2.fractionValue.numerator * f1.fractionValue.denominator;
        let commonDenominator = f2.fractionValue.denominator * f1.fractionValue.denominator;
        correct = f1Numerator + f2Numerator === commonDenominator;

        if (correct)
        {
            this.correctCount++;
            ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._correct, this);
            this.doCorrect();
        }
        else
        {
            this.wrongCount++; ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._incorrect, this);
            this.doIncorrect();
        }
    }

    doCorrect()
    {
        if (this.correctCount >= this.gridSize * 2)
        {
            this.hud.stopTime();
        }
        this.gridDisplay.showCorrect(this.selectedTiles);
    }

    showCorrectDisplay()
    {
        let len = this.selectedTiles.length;
        for (let i = 0; i < len; i++)
        {
            this.selectedTiles[i].discardTile();
        }
        let pt = this.hud.getMonsterLoc();
        this.combineContainer.startCombine(this.selectedTiles, pt);
        this.combineContainer.setVisible(true);
    }

    feedMonster()
    {
        this.hud.feedHim();
    }

    monsterFinishedEating()
    {
        if (this.correctCount >= this.gridSize * 2)
        {

            this.doGameWin();
        }
        else
        {
            this.selectedTiles = [];
            this.busyAnimating = false;
            this.gridDisplay.unhold();
        }
    }

    doGameWin()
    {
        this.showGameWin();
    }

    showGameWin()
    {
        ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._hooray, this);
        let nextime = this.hud.showGameWin(0, this.gridDisplay.x + this.gridDisplay.workingWidth * this.gridDisplay.scaleX / 2, this.gridDisplay.y + this.gridDisplay.workingHeight * this.gridDisplay.scaleY / 2);
        this.children.bringToTop(this.hud);
        gameData.userData.score = this.hud.curTime;
        gameData.userData.didWin = true;
        this.time.delayedCall(nextime, () => this.scene.start(Results.KEY));
    }

    doIncorrect()
    {
        this.gridDisplay.showIncorrect(this.selectedTiles);
        let wait = this.hud.showStrike(this.wrongCount);
        if (this.wrongCount >= 3)
        {
            this.doGameOver(wait);
        }
    }

    resetSelected()
    {
        let len = this.selectedTiles.length;
        for (let i = 0; i < len; i++)
        {
            this.selectedTiles[i].resetDisplay();
        }
        this.selectedTiles = [];
        this.busyAnimating = false;
        this.gridDisplay.unhold();
    }

    doGameOver(wait)
    {
        this.hud.stopTime();
        this.busyAnimating = true;
        this.gameOver = true;
        this.time.delayedCall(wait, this.showGameOver, null, this);
    }

    showGameOver()
    {
        ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._die, this, 1, false, 0.62);
        let waittime = this.gridDisplay.strikeOutTransition();
        let nextime = this.hud.showStrikeOut(waittime, this.gridDisplay.x + this.gridDisplay.workingWidth * this.gridDisplay.scaleX / 2, this.gridDisplay.y + this.gridDisplay.workingHeight * this.gridDisplay.scaleY / 2);
        this.children.bringToTop(this.hud);
        gameData.userData.didWin = false;
        this.time.delayedCall(nextime + 320, () => this.scene.start(Results.KEY));
    }
}

class RandomValuePool
{
    constructor(values)
    {
        this.pool = null;
        this.values = values.slice();
        this.setup();
    }

    setup()
    {
        this.restart();
    }

    getValue()
    {
        if (this.pool.length <= 0)
        {
            this.restart();
        }
        return this.pool.pop();
    }

    restart()
    {
        this.pool = this.values.slice();
        Phaser.Utils.Array.Shuffle(this.pool);
    }
}